import React, {useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {FiMinus, FiShoppingCart} from 'react-icons/fi';

import {useCart} from '../context/CartContext';

const normalizePrice = price => {
  if (price === null || price === undefined || price === '') {
    return 0;
  }

  const cleaned =
    typeof price === 'string' ? price.replace(',', '.').trim() : price;

  const numericPrice = Number(cleaned);

  if (Number.isNaN(numericPrice)) {
    return 0;
  }

  if (Number.isInteger(numericPrice) && numericPrice >= 1000) {
    return numericPrice / 100;
  }

  return numericPrice;
};

const formatPrice = price => `R ${normalizePrice(price).toFixed(2)}`;

const CartBannerWeb = ({navigate, onClose}) => {
  const {cartItems} = useCart();

  const [visible, setVisible] = useState(true);
  const [minimised, setMinimised] = useState(false);

  const activeCartItems = cartItems.filter(item => item.quantity > 0);

  if (!activeCartItems.length || !visible) {
    return null;
  }

  const totalQuantity = activeCartItems.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  const total = activeCartItems.reduce((acc, item) => {
    return acc + normalizePrice(item.price) * item.quantity;
  }, 0);

  const latestItem = activeCartItems[activeCartItems.length - 1];

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  if (minimised) {
    return (
      <button
        type="button"
        className={css(styles.minimisedButton)}
        onClick={() => setMinimised(false)}
        aria-label="Expand basket">
        <span className={css(styles.minimisedIcon)}><FiShoppingCart /></span>

        <span className={css(styles.minimisedCount)}>{totalQuantity}</span>
      </button>
    );
  }

  return (
    <div className={css(styles.banner)}>
      <div className={css(styles.left)}>
        <div className={css(styles.iconBadge)}><FiShoppingCart /></div>

        <div className={css(styles.summary)}>
          <p className={css(styles.kicker)}>Basket active</p>

          <h3 className={css(styles.title)}>
            {totalQuantity} item{totalQuantity === 1 ? '' : 's'} in basket
          </h3>

          <p className={css(styles.itemName)}>
            Latest: {latestItem?.name || 'Selected item'}
          </p>
        </div>
      </div>

      <div className={css(styles.middle)}>
        <span className={css(styles.totalLabel)}>Total</span>
        <strong className={css(styles.totalValue)}>{formatPrice(total)}</strong>
      </div>

      <div className={css(styles.right)}>
        <button
          type="button"
          className={css(styles.iconButton)}
          onClick={() => setMinimised(true)}
          aria-label="Minimise basket">
          <FiMinus />
        </button>

        <button
          type="button"
          className={css(styles.iconButton)}
          onClick={handleClose}
          aria-label="Close basket banner">
          ×
        </button>

        <button
          type="button"
          className={css(styles.viewCartBtn)}
          onClick={() => navigate('/cart')}>
          View Basket
        </button>
      </div>
    </div>
  );
};

const styles = StyleSheet.create({
  banner: {
    position: 'fixed',
    left: '20px',
    right: '20px',
    bottom: '22px',
    maxWidth: '760px',
    margin: '0 auto',
    backgroundColor: '#0F3D2E',
    border: '1px solid rgba(201, 161, 74, 0.55)',
    borderRadius: '28px',
    padding: '14px',
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 18px 44px rgba(15, 61, 46, 0.24)',
    zIndex: 999,
    boxSizing: 'border-box',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',

    '@media (max-width: 720px)': {
      left: '14px',
      right: '14px',
      bottom: '16px',
      gridTemplateColumns: '1fr',
      gap: '12px',
      borderRadius: '24px',
    },
  },

  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },

  iconBadge: {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    borderRadius: '50%',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },

  summary: {
    minWidth: 0,
  },

  kicker: {
    margin: '0 0 3px',
    color: '#C9A14A',
    fontSize: '10px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },

  title: {
    margin: 0,
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '900',
    lineHeight: 1.25,
  },

  itemName: {
    margin: '3px 0 0',
    color: '#F4EBD8',
    fontSize: '12px',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '290px',

    '@media (max-width: 720px)': {
      maxWidth: '100%',
      whiteSpace: 'normal',
    },
  },

  middle: {
    backgroundColor: 'rgba(255, 253, 247, 0.08)',
    border: '1px solid rgba(230, 216, 190, 0.22)',
    borderRadius: '18px',
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '112px',

    '@media (max-width: 720px)': {
      width: '100%',
      boxSizing: 'border-box',
    },
  },

  totalLabel: {
    color: '#F4EBD8',
    fontSize: '11px',
    fontWeight: '700',
  },

  totalValue: {
    color: '#C9A14A',
    fontSize: '17px',
    fontWeight: '900',
    whiteSpace: 'nowrap',
  },

  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',

    '@media (max-width: 720px)': {
      width: '100%',
      justifyContent: 'space-between',
    },
  },

  iconButton: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    border: '1px solid rgba(230, 216, 190, 0.22)',
    backgroundColor: 'rgba(255, 253, 247, 0.08)',
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '900',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,

    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.14)',
    },
  },

  viewCartBtn: {
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    padding: '12px 16px',
    borderRadius: '18px',
    fontSize: '13px',
    fontWeight: '900',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 10px 24px rgba(201, 161, 74, 0.18)',

    ':hover': {
      transform: 'translateY(-1px)',
    },

    '@media (max-width: 720px)': {
      flex: 1,
    },
  },

  minimisedButton: {
    position: 'fixed',
    right: '22px',
    bottom: '22px',
    width: '62px',
    height: '62px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    border: '2px solid #C9A14A',
    color: '#C9A14A',
    boxShadow: '0 18px 44px rgba(15, 61, 46, 0.24)',
    zIndex: 999,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    ':hover': {
      transform: 'translateY(-2px)',
    },

    '@media (max-width: 720px)': {
      right: '16px',
      bottom: '16px',
      width: '58px',
      height: '58px',
    },
  },

  minimisedIcon: {
    fontSize: '24px',
  },

  minimisedCount: {
    position: 'absolute',
    top: '-5px',
    right: '-5px',
    minWidth: '24px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    border: '1px solid #FFFDF7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '900',
    padding: '0 6px',
    boxSizing: 'border-box',
  },
});

export default CartBannerWeb;
